import axios from 'axios';
import API_CONFIG from '../config/apiConfig';

const API_BASE_URL = API_CONFIG.BASE_URL;

export interface TokenUsageData {
  provider: string;
  modelName: string;
  inputTokens: number;
  outputTokens: number;
  conversationId?: string;
  requestId?: string;
}

export interface ApiKeyData {
  provider: string;
  keyName: string;
  apiKey: string;
  isDefault?: boolean;
}

export interface NotificationSettingsData {
  threshold: number;
  email?: string;
  enabled?: boolean;
}

class TokenUsageApi {
  private getHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  async recordTokenUsage(data: TokenUsageData) {
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/token-usage`,
      data,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async getUsageSummary(startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await axios.get(
      `${API_BASE_URL}/api/v1/token-usage/summary?${params}`,
      { headers: this.getHeaders() }
    );
    return response;
  }

  async getPricing() {
    const response = await axios.get(
      `${API_BASE_URL}/api/v1/token-usage/pricing`,
      { headers: this.getHeaders() }
    );
    return response;
  }

  async saveApiKey(data: ApiKeyData) {
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/token-usage/api-keys`,
      data,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async getApiKeys() {
    const response = await axios.get(
      `${API_BASE_URL}/api/v1/token-usage/api-keys`,
      { headers: this.getHeaders() }
    );
    return response;
  }

  async deleteApiKey(id: string) {
    const response = await axios.delete(
      `${API_BASE_URL}/api/v1/token-usage/api-keys/${id}`,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async purchasePrepaid(provider: string, amount: number, stripePaymentIntentId?: string) {
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/token-usage/prepaid`,
      { provider, amount, stripePaymentIntentId },
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async getPrepaidBalance() {
    const response = await axios.get(
      `${API_BASE_URL}/api/v1/token-usage/prepaid/balance`,
      { headers: this.getHeaders() }
    );
    return response;
  }

  async updateNotificationSettings(data: NotificationSettingsData) {
    const response = await axios.put(
      `${API_BASE_URL}/api/v1/token-usage/notifications`,
      data,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async getNotificationSettings() {
    const response = await axios.get(
      `${API_BASE_URL}/api/v1/token-usage/notifications`,
      { headers: this.getHeaders() }
    );
    return response;
  }
}

export const tokenUsageApi = new TokenUsageApi();